import { Button, Icon } from "@sellar-io/components";
import { ActionPanel } from "../actionPanel/ActionPanel";
import {
  OrderGetResponseType,
  SupplierRelationshipGetResponseType,
} from "@sellar-io/api-v2/types";
import { useNavigate } from "react-router";
import { DISTRIBUTION_PALLET_SHIPMENT_DETAILS } from "../../constants/routes";
import { Alert } from "../alert/Alert";
import dayjs from "dayjs";

type SellarDistributionPalletActionPanelProps = {
  order: Pick<
    OrderGetResponseType,
    | "dispatchedAt"
    | "deliveredAt"
    | "SupplierPalletShipment"
    | "paymentStatus"
    | "paymentType"
    | "paymentDue"
    | "stripeInvoiceId"
    | "invoiceIssuedAt"
    | "nextPalletShipmentCutoffAt"
  >;
  connection: Pick<SupplierRelationshipGetResponseType, "paymentTerms">;
  onClickMarkAsPaid: () => void;
};

export const SellarDistributionPalletActionPanel = ({
  order,
  connection,
  onClickMarkAsPaid,
}: SellarDistributionPalletActionPanelProps) => {
  const { SupplierPalletShipment: supplierPalletShipment } = order;

  if (order.deliveredAt) {
    return null;
  }

  if (
    isPalletOrderPaymentDue({ order, connection }) &&
    !orderHasPalletOrderShipment(supplierPalletShipment)
  ) {
    return (
      <PalletOrderPaymentDue
        onClickMarkAsPaid={onClickMarkAsPaid}
        nextOrderCutoffDate={order.nextPalletShipmentCutoffAt}
      />
    );
  }

  if (isOrderDispatched(order)) {
    return <PalletOrderReceivedAtHub />;
  }

  if (isPalletOrderInTransit(supplierPalletShipment)) {
    return <PalletOrderInTransit />;
  }

  if (
    orderHasPalletOrderShipment(supplierPalletShipment) &&
    isPalletOrderReadyToShip(supplierPalletShipment)
  ) {
    return (
      <PalletOrderReadyToShip palletShipmentId={supplierPalletShipment.id} />
    );
  }

  if (
    orderHasPalletOrderShipment(supplierPalletShipment) &&
    isPalletOrderAccepted(supplierPalletShipment)
  ) {
    return <PalletOrderAccepted palletShipmentId={supplierPalletShipment.id} />;
  }

  return null;
};

const PalletOrderPaymentDue = ({
  onClickMarkAsPaid,
  nextOrderCutoffDate,
}: {
  onClickMarkAsPaid: () => void;
  nextOrderCutoffDate: Date | null | undefined;
}) => {
  const nextOrderCutoffDateFormatted = nextOrderCutoffDate
    ? dayjs(nextOrderCutoffDate).format("HH:mma dddd Do MMMM")
    : "the cut-off";

  return (
    <Alert
      className={"w-full"}
      title={`Action required. Finalise this order or mark it as paid before ${nextOrderCutoffDateFormatted}.`}
      message={
        "Orders with customers who pay pro-forma need to be either finalised manually or paid before the cut-off time to include them with your pallet shipment."
      }
      type={"warning"}
    >
      <div className="flex justify-between gap-2">
        <Button
          color={"secondary"}
          className={"border-orange-300 bg-orange-100 text-orange-800"}
          onClick={onClickMarkAsPaid}
        >
          Mark as paid
        </Button>
        <Button
          onClick={() =>
            window.open(
              "https://intercom.help/sellario/en/articles/13726326-getting-your-orders-to-us#h_431e618b3d",
              "_blank",
            )
          }
          color="transparent"
          className="text-amber-800 hover:bg-transparent"
        >
          Find out more <Icon icon="ARROW_TOP_RIGHT" />
        </Button>
      </div>
    </Alert>
  );
};

const PalletOrderAccepted = ({
  palletShipmentId,
}: {
  palletShipmentId: number;
}) => {
  const navigate = useNavigate();

  return (
    <ActionPanel
      className="flex flex-col gap-4"
      title="SD Pallet order accepted"
      message={`As this is an SD Pallet order, you can finalise it now or it will be automatically finalised on your behalf at the delivery schedule cut-off point. We will notify you when it’s time to get the order items ready to ship.`}
    >
      <div className="flex flex-wrap gap-2">
        <Button
          color="primary"
          onClick={() =>
            navigate(
              DISTRIBUTION_PALLET_SHIPMENT_DETAILS.href({
                palletShipmentId,
              }),
            )
          }
        >
          SD Pallet Shipments
          <Icon size={0.7} icon="CHEVRON_RIGHT" />
        </Button>
        <Button
          color="secondary"
          onClick={() =>
            window.open(
              "https://intercom.help/sellario/en/articles/13726326-getting-your-orders-to-us#h_431e618b3d",
              "_blank",
            )
          }
        >
          Find out more
        </Button>
      </div>
    </ActionPanel>
  );
};

const PalletOrderReadyToShip = ({
  palletShipmentId,
}: {
  palletShipmentId: number;
}) => {
  const navigate = useNavigate();

  return (
    <ActionPanel
      className="flex flex-col gap-4"
      title="Ready to ship"
      message={`This SD Pallet order has been finalised and is ready to ship. Head over to the ‘SD Pallet Shipments’ page to see a comprehensive pick list of all the stock you need to get ready as part of this shipment`}
    >
      <div className="flex gap-2">
        <Button
          color="primary"
          onClick={() =>
            navigate(
              DISTRIBUTION_PALLET_SHIPMENT_DETAILS.href({
                palletShipmentId,
              }),
            )
          }
        >
          SD Pallet Shipments
          <Icon size={0.7} icon="CHEVRON_RIGHT" />
        </Button>
      </div>
    </ActionPanel>
  );
};

const PalletOrderInTransit = () => {
  return (
    <ActionPanel
      className="flex flex-col gap-4"
      title="In transit"
      message={`This SD Pallet order has been picked up from your premises and is on it’s way to our hub. This order can no longer be amended or cancelled.`}
    />
  );
};

const PalletOrderReceivedAtHub = () => {
  return (
    <ActionPanel
      className="flex flex-col gap-4"
      title="Order dispatched"
      message={`This SD Pallet order has arrived at the SD hub and is awaiting delivery by the SD team.`}
    />
  );
};

const isOrderDispatched = (order: { dispatchedAt: Date | null }) => {
  return !!order.dispatchedAt;
};

const isPalletOrderInTransit = (
  supplierPalletShipment: Pick<
    NonNullable<OrderGetResponseType["SupplierPalletShipment"]>,
    "dispatchedAt"
  > | null,
) => {
  return !!supplierPalletShipment?.dispatchedAt;
};

const isPalletOrderReadyToShip = (
  supplierPalletShipment: Pick<
    NonNullable<OrderGetResponseType["SupplierPalletShipment"]>,
    "finalisedAt"
  > | null,
) => {
  return !!supplierPalletShipment?.finalisedAt;
};

const isPalletOrderAccepted = (
  supplierPalletShipment: Pick<
    NonNullable<OrderGetResponseType["SupplierPalletShipment"]>,
    "finalisedAt" | "id"
  > | null,
) => {
  return !!supplierPalletShipment?.id && !supplierPalletShipment.finalisedAt;
};

const orderHasPalletOrderShipment = (
  supplierPalletShipment: Pick<
    NonNullable<OrderGetResponseType["SupplierPalletShipment"]>,
    "id"
  > | null,
): supplierPalletShipment is NonNullable<
  OrderGetResponseType["SupplierPalletShipment"]
> => {
  return !!supplierPalletShipment?.id;
};

const isPalletOrderPaymentDue = ({
  order,
  connection,
}: {
  order: Pick<
    OrderGetResponseType,
    "paymentStatus" | "paymentType" | "paymentDue" | "stripeInvoiceId"
  >;
  connection: Pick<SupplierRelationshipGetResponseType, "paymentTerms">;
}) => {
  const isPaid = order.paymentStatus === "paid";
  const isCardPaymentWithDeposit =
    order.paymentType === "cardPaymentWithDeposit";
  const isPaymentOnDelivery = connection.paymentTerms === "paymentOnDelivery";
  const hasCreditPaymentTerms = connection.paymentTerms === "credit";
  const isSellarCreditPayment = order.paymentType === "sellarCredit";

  return (
    !isPaid &&
    !isCardPaymentWithDeposit &&
    !isPaymentOnDelivery &&
    !isSellarCreditPayment &&
    !hasCreditPaymentTerms
  );
};
